/**
 * Application ↔ Vital unit conversions (application-owned math).
 * Ranges aligned with pinned Vital metadata in @defibrillator/vital-schema-gen.
 */
export class ConversionError extends Error {
    constructor(message) {
        super(message);
        this.name = "ConversionError";
    }
}
export const VITAL_LFO_FREQUENCY_MIN = -7;
export const VITAL_LFO_FREQUENCY_MAX = 9;
export const VITAL_ENV_TIME_MAX = 2.37842;
export function hzToVitalLfoFrequency(hz) {
    if (!(hz > 0) || !Number.isFinite(hz)) {
        throw new ConversionError(`LFO Hz not representable: ${hz}`);
    }
    const value = Math.log2(hz);
    if (value < VITAL_LFO_FREQUENCY_MIN || value > VITAL_LFO_FREQUENCY_MAX) {
        throw new ConversionError(`LFO Hz out of Vital range: ${hz}`);
    }
    return value;
}
export function vitalLfoFrequencyToHz(value) {
    if (!Number.isFinite(value) || value < VITAL_LFO_FREQUENCY_MIN || value > VITAL_LFO_FREQUENCY_MAX) {
        throw new ConversionError(`Vital LFO frequency out of range: ${value}`);
    }
    return 2 ** value;
}
export function tempoDivisionToVitalIndex(index) {
    if (!Number.isInteger(index) || index < 0 || index > 12) {
        throw new ConversionError(`Tempo division index not representable: ${index}`);
    }
    return index;
}
export function vitalIndexToTempoDivision(index) {
    return tempoDivisionToVitalIndex(index);
}
export function hzToVitalCutoff(hz) {
    if (!(hz > 0) || !Number.isFinite(hz)) {
        throw new ConversionError(`Cutoff Hz not representable: ${hz}`);
    }
    const midi = 12 * Math.log2(hz / 440) + 69;
    if (midi < 8 || midi > 136) {
        throw new ConversionError(`Cutoff Hz out of Vital MIDI range: ${hz}`);
    }
    return midi;
}
export function vitalCutoffToHz(midi) {
    if (!Number.isFinite(midi) || midi < 8 || midi > 136) {
        throw new ConversionError(`Vital cutoff out of range: ${midi}`);
    }
    return 440 * 2 ** ((midi - 69) / 12);
}
export function gainToVitalVolume(gain01) {
    if (!Number.isFinite(gain01) || gain01 < 0 || gain01 > 1) {
        throw new ConversionError(`Gain not representable: ${gain01}`);
    }
    if (gain01 <= 0)
        return 0;
    const db = 20 * Math.log10(gain01);
    const clampedDb = Math.max(-80, Math.min(6, db));
    return (clampedDb + 80) ** 2;
}
export function vitalVolumeToGain(vital) {
    if (!Number.isFinite(vital) || vital < 0) {
        throw new ConversionError(`Vital volume not representable: ${vital}`);
    }
    if (vital <= 0)
        return 0;
    const db = Math.sqrt(vital) - 80;
    return Math.min(1, Math.max(0, 10 ** (db / 20)));
}
export function secondsToVitalEnvTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0 || seconds > 32) {
        throw new ConversionError(`Envelope seconds not representable: ${seconds}`);
    }
    if (seconds <= 0)
        return 0;
    return Math.min(VITAL_ENV_TIME_MAX, seconds ** 0.25);
}
export function vitalEnvTimeToSeconds(value) {
    if (!Number.isFinite(value) || value < 0 || value > VITAL_ENV_TIME_MAX) {
        throw new ConversionError(`Vital env time out of range: ${value}`);
    }
    return value ** 4;
}
export function unisonDetuneToVital(detune01) {
    if (!Number.isFinite(detune01) || detune01 < 0 || detune01 > 1) {
        throw new ConversionError(`Unison detune not representable: ${detune01}`);
    }
    return detune01;
}
export function vitalToUnisonDetune(value) {
    return unisonDetuneToVital(value);
}
export function hzToVitalExponentialRate(hz, min = -7, max = 9) {
    if (!(hz > 0) || !Number.isFinite(hz)) {
        throw new ConversionError(`Rate Hz not representable: ${hz}`);
    }
    const value = Math.log2(hz);
    if (value < min || value > max) {
        throw new ConversionError(`Rate Hz out of range: ${hz}`);
    }
    return value;
}
export function vitalExponentialRateToHz(value, min = -7, max = 9) {
    if (!Number.isFinite(value) || value < min || value > max) {
        throw new ConversionError(`Vital exponential rate out of range: ${value}`);
    }
    return 2 ** value;
}
//# sourceMappingURL=conversions.js.map